/**
 * iter-170al — Roles & Permissions matrix.
 *
 * Lists the user's spaces (personal + teams), shows the active
 * space's members with role chips + remove buttons, lists pending
 * invitations, and exposes an invite form.
 *
 * Lives inside ``<LockedSection featureKey="roles_permissions">``.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Users, Trash2, Loader2, Mail, ShieldCheck, ShieldAlert, Eye } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { useSpace } from "../contexts/SpaceContext";
import { tx } from "../i18n";
import { api } from "../lib/api";
import { toast } from "sonner";

const ROLE_META = {
  owner:  { Icon: ShieldCheck, color: "text-violet-700",  bg: "bg-violet-50",  border: "border-violet-200" },
  editor: { Icon: ShieldAlert, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  viewer: { Icon: Eye,         color: "text-slate-600",   bg: "bg-slate-50",   border: "border-slate-200" },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.viewer;
  const Icon = m.Icon;
  return (
    <Badge
      variant="outline"
      className={`gap-1 ${m.bg} ${m.color} ${m.border} text-[10px] uppercase tracking-wider`}
    >
      <Icon className="h-3 w-3" />{role}
    </Badge>
  );
}

export function RolesPermissionsCard() {
  const { lang } = useLang();
  const qc = useQueryClient();
  // iter-170am-part6 — Always operate on the user's ACTIVE workspace
  // (single source of truth = the global SpaceSwitcher). We previously
  // had a local picker; that was removed to keep one entry point.
  const { activeSpaceId } = useSpace();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");

  const { data: spacesData } = useQuery({
    queryKey: ["my-spaces"],
    queryFn: async () => (await api.get("/spaces")).data,
    retry: false,
  });
  const spaces = spacesData?.items || [];

  // Effective space = the active one from the sidebar context; fallback
  // to the personal/first space if the context hasn't resolved yet.
  const fallbackSpace = (() => {
    if (!spaces.length) return null;
    const personal = spaces.find((s) => s.kind === "personal");
    return (personal || spaces[0]).id;
  })();
  const effectiveSpace = activeSpaceId || fallbackSpace;

  const isOwner = spaces.find((s) => s.id === effectiveSpace)?.my_role === "owner";

  const { data: membersData } = useQuery({
    queryKey: ["space-members", effectiveSpace],
    queryFn: async () => (await api.get(`/spaces/${effectiveSpace}/members`)).data,
    enabled: !!effectiveSpace,
    retry: false,
  });
  const { data: invitesData } = useQuery({
    queryKey: ["space-invites", effectiveSpace],
    queryFn: async () => (await api.get(`/spaces/${effectiveSpace}/invitations`)).data,
    enabled: !!effectiveSpace && isOwner,
    retry: false,
  });
  const members = membersData?.items || [];
  const invites = (invitesData?.items || []).filter((i) => !i.accepted_at && !i.revoked_at);

  const inviteMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/spaces/${effectiveSpace}/invitations`,
        { email: inviteEmail.trim(), role: inviteRole })).data,
    onSuccess: () => {
      setInviteEmail("");
      qc.invalidateQueries({ queryKey: ["space-invites", effectiveSpace] });
      toast.success(tx(lang, "Invite sent", "Invitación enviada", "Convite enviado"));
    },
    onError: () => toast.error(tx(lang, "Invite failed", "Error al invitar", "Erro ao convidar")),
  });

  const roleMut = useMutation({
    mutationFn: async ({ userId, role }) =>
      (await api.patch(`/spaces/${effectiveSpace}/members/${userId}`, { role })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["space-members", effectiveSpace] }),
    onError: (e) => toast.error(e?.response?.data?.detail || "error"),
  });

  const removeMut = useMutation({
    mutationFn: async (userId) =>
      (await api.delete(`/spaces/${effectiveSpace}/members/${userId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["space-members", effectiveSpace] }),
    onError: (e) => toast.error(e?.response?.data?.detail || "error"),
  });

  const revokeInviteMut = useMutation({
    mutationFn: async (invId) =>
      (await api.delete(`/spaces/${effectiveSpace}/invitations/${invId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["space-invites", effectiveSpace] }),
  });

  return (
    <div className="space-y-4" data-testid="roles-permissions-card">
      {/* iter-170am-part6 — "Tus espacios" section removed.
          Space switching now happens via the global SpaceSwitcher in
          the sidebar; the tab Usuarios just configures members of the
          currently-active workspace. */}

      {/* Members + invite */}
      {effectiveSpace && (
        <Card className="p-5 rounded-2xl space-y-4" data-testid="rp-members-card">
          <h3 className="font-semibold text-slate-900">
            {tx(lang, "Members", "Miembros", "Membros")}
          </h3>

          {/* Invite form */}
          {isOwner && (
            <div className="flex gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50">
              <Input
                type="email"
                placeholder={tx(lang, "Invite by email", "Invitar por email", "Convidar por email")}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="rp-invite-email"
                className="flex-1"
              />
              <select /* design-system: ALLOW legacy-todo */
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                data-testid="rp-invite-role"
                className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              >
                <option value="editor">editor</option>
                <option value="viewer">viewer</option>
              </select>
              <Button
                onClick={() => inviteMut.mutate()}
                disabled={!inviteEmail.trim() || inviteMut.isPending}
                className="gap-1.5"
                data-testid="rp-invite-btn"
              >
                {inviteMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                {tx(lang, "Invite", "Invitar", "Convidar")}
              </Button>
            </div>
          )}

          {/* Members list */}
          <ul className="space-y-2" data-testid="rp-members-list">
            {members.map((m) => (
              <li
                key={m.user_id}
                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white"
                data-testid={`rp-member-${m.user_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {m.name || m.email || m.user_id.slice(0, 8)}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{m.email}</div>
                </div>
                <RoleBadge role={m.role} />
                {isOwner && m.role !== "owner" && (
                  <>
                    <select /* design-system: ALLOW legacy-todo */
                      value={m.role}
                      onChange={(e) => roleMut.mutate({ userId: m.user_id, role: e.target.value })}
                      data-testid={`rp-member-role-${m.user_id}`}
                      className="h-7 rounded border border-slate-200 px-2 text-xs bg-white"
                    >
                      <option value="editor">editor</option>
                      <option value="viewer">viewer</option>
                    </select>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => removeMut.mutate(m.user_id)}
                      className="h-7 px-2 text-rose-700 border-rose-200 hover:bg-rose-50"
                      data-testid={`rp-member-remove-${m.user_id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>

          {/* Pending invites */}
          {isOwner && invites.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                {tx(lang, "Pending invitations", "Invitaciones pendientes", "Convites pendentes")}
              </p>
              <ul className="space-y-1.5" data-testid="rp-invites-list">
                {invites.map((i) => (
                  <li
                    key={i.id}
                    className="flex items-center gap-3 p-2 rounded-xl border border-amber-200 bg-amber-50/50 text-xs"
                  >
                    <Mail className="h-3.5 w-3.5 text-amber-700" />
                    <span className="flex-1 truncate">{i.email}</span>
                    <RoleBadge role={i.role} />
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => revokeInviteMut.mutate(i.id)}
                      className="h-6 px-2 text-rose-700"
                      data-testid={`rp-invite-revoke-${i.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
